import { useEffect, useId, useRef, useState } from "react";
import { FaInstagram } from "react-icons/fa";
import { SlLocationPin } from "react-icons/sl";
import { TbFileDescription } from "react-icons/tb";
import { InputWithIcon } from "../../../components/inputs/inputWithIcon";
import { IStoreSaude } from "../../../model/store";
import { StoreCard } from "../../categories/components/storeCard";
import { AddAssociatedWrapper, AddConveniadoForm } from "./styles";

import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { MdOutlineStorefront } from "react-icons/md";
import { PurpleButton } from "../../../components/buttons/purpleButton";
import { ImageUpload } from "../../../components/imageUpload";
import { useUploadingState } from "../../../hooks/useUploadingState";
import { IModalidade } from "../../../model/modalidade";
import conveniadoService from "../../../services/conveniadoService";
import modalidadeSaudeService from "../../../services/modalidadeSaudeService";

const INITIAL_MODALIDADE = { id: 0, nome: "" };
const ERROR_MESSAGE =
  "Ocorreu um erro ao adicionar conveniado saúde. Por favor, tente novamente.";

export const AddAssociatedSaude = () => {
  const toast = useRef<Toast>(null);
  const modalidadeInputId = useId();

  const [nome, setNome] = useState("");
  const [endereco, setEndereco] = useState("");
  const [modalidade, setModalidade] = useState<IModalidade>(INITIAL_MODALIDADE);
  const [modalidadeId, setModalidadeId] = useState<number>(0);
  const [descricao, setDescricao] = useState("");
  const [instagram, setInstagram] = useState("");
  const [site, setSite] = useState("");
  const [imagem, setImagem] = useState("");
  const [modalidadesOptions, setModalidadesOptions] = useState<
    Array<IModalidade>
  >([]);
  const { isUploading, uploadingRef, onUploadingChange } =
    useUploadingState();

  useEffect(() => {
    let active = true;

    const getModalidades = async () => {
      try {
        const response = await modalidadeSaudeService.getModalidadeSaude();
        if (active) {
          setModalidadesOptions(response.data);
        }
      } catch {
        if (active) {
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: "Falha ao obter modalidades.",
            life: 3000,
          });
        }
      }
    };

    void getModalidades();
    return () => {
      active = false;
    };
  }, []);

  async function createAssociated() {
    if (uploadingRef.current) {
      showUploadInProgressWarning();
      return;
    }

    if (isRequiredFieldsEmpty()) {
      return;
    }

    const store: Partial<IStoreSaude> = {
      nome,
      endereco,
      modalidadeSaude: { id: modalidadeId },
      descricao,
      instagram,
      site,
      imagem,
    };

    try {
      const response = await conveniadoService.saveConveniado(store);
      if (response.status === 200) {
        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: `Conveniado Saúde ${nome} adicionado com sucesso!`,
          life: 3000,
        });
        cleanStates();
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: ERROR_MESSAGE,
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Erro ao adicionar conveniado saúde:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: ERROR_MESSAGE,
        life: 3000,
      });
    }
  }

  function isRequiredFieldsEmpty() {
    if (imagem.length > 255) {
      showInfoToastMessage("A imagem não pode ultrapassar 255 caracteres.");
      return true;
    }
    const requiredFields = [nome, imagem, descricao, endereco];
    const isEmpty = requiredFields.some((field) => field.trim().length === 0);
    if (isEmpty || modalidadeId === 0) {
      showInfoToastMessage("Por favor, preencha todos os campos obrigatórios.");
      return true;
    }
    return false;
  }

  function showInfoToastMessage(detail: string) {
    toast.current?.show({
      severity: "info",
      summary: "Info",
      detail,
      life: 3000,
    });
  }

  function showUploadInProgressWarning() {
    toast.current?.show({
      severity: "warn",
      summary: "Atenção",
      detail: "Aguarde o envio da imagem terminar.",
      life: 3000,
    });
  }

  function cleanStates() {
    const states = [
      setNome,
      setEndereco,
      setDescricao,
      setInstagram,
      setSite,
      setImagem,
    ];
    states.forEach((state) => state(""));
    setModalidade(INITIAL_MODALIDADE);
    setModalidadeId(0);
  }

  function handleModalidadeChange(value: number) {
    setModalidadeId(value);
    const selectedModalidade = modalidadesOptions.find(
      (option) => option.id === value
    );
    if (selectedModalidade) {
      setModalidade(selectedModalidade);
    }
  }

  return (
    <>
      <Toast ref={toast} />
      <AddAssociatedWrapper>
        <AddConveniadoForm>
          <div
            style={{
              margin: "24px 0px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2 style={{ width: "fit-content" }}>Adicionar Conveniado Saúde</h2>
          </div>
          <InputWithIcon
            required={true}
            title="Conveniado"
            icon={<MdOutlineStorefront size={24} color="#ff3333bd" />}
            placeholder="Digite o nome do conveniado saúde"
            value={nome}
            onChange={setNome}
          />

          <label htmlFor={modalidadeInputId}>Selecione uma modalidade: *</label>
          <Dropdown
            inputId={modalidadeInputId}
            filter={true}
            value={modalidadeId}
            onChange={(e: DropdownChangeEvent) =>
              handleModalidadeChange(e.value)
            }
            options={modalidadesOptions}
            optionLabel="nome"
            optionValue="id"
            placeholder="Selecione uma modalidade"
          />

          <ImageUpload
            required={true}
            label="Imagem"
            category="CONVENIADOS"
            value={imagem}
            onChange={setImagem}
            onUploadingChange={onUploadingChange}
          />
          <InputWithIcon
            required={true}
            title="Descrição"
            icon={<TbFileDescription size={24} color="#ff3333bd" />}
            placeholder="Digite a descrição do conveniado"
            value={descricao}
            onChange={setDescricao}
          />

          <InputWithIcon
            title="Instagram"
            icon={<FaInstagram size={24} color="#ff3333bd" />}
            placeholder="Digite o @ do instagram do conveniado"
            value={instagram}
            onChange={setInstagram}
          />
          <InputWithIcon
            title="Site"
            icon={<MdOutlineStorefront size={24} color="#ff3333bd" />}
            placeholder="Digite a url do site do conveniado"
            value={site}
            onChange={setSite}
          />
          <InputWithIcon
            required={true}
            title="Endereço"
            icon={<SlLocationPin size={24} color="#ff3333bd" />}
            placeholder="Digite o endereço do conveniado"
            value={endereco}
            onChange={setEndereco}
          />
        </AddConveniadoForm>
        <div style={{ maxHeight: "502px" }}>
          <StoreCard
            endereco={endereco}
            modalidade={modalidade}
            descricao={descricao}
            imagem={imagem}
            instagram={instagram}
            nome={nome}
            site={site}
            id={0}
          />
        </div>
        <PurpleButton
          title="Adicionar"
          onClick={() => void createAssociated()}
          disabled={isUploading}
        />
      </AddAssociatedWrapper>
    </>
  );
};
